import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Volume2, Play, Square, Info } from 'lucide-react';

// NCLEX Lung Sounds Library - with real audio files
const LUNG_SOUNDS = {
  normal: {
    name: "Normal (Vesicular)",
    description: "Soft, low-pitched sounds heard over most of the lung fields. Inspiration is longer than expiration.",
    clinical: "Normal finding - indicates clear airways and proper ventilation",
    audioFile: "/sounds/lungs/normal.mp3",
    useRealAudio: true,
    duration: 12000
  },
  crackles_fine: {
    name: "Fine Crackles (Rales)",
    description: "High-pitched, discontinuous popping sounds heard during late inspiration. Sound like hair rubbing together.",
    clinical: "Associated with: Pulmonary fibrosis, early CHF, pneumonia, atelectasis",
    nclex_tip: "Fine crackles do NOT clear with coughing",
    audioFile: "/sounds/lungs/fine_crackles.mp3",
    useRealAudio: true,
    duration: 12000
  },
  crackles_coarse: {
    name: "Coarse Crackles",
    description: "Low-pitched, bubbling, gurgling sounds heard during inspiration and sometimes expiration.",
    clinical: "Associated with: Pulmonary edema, pneumonia, bronchitis, COPD with secretions",
    nclex_tip: "May clear or change with coughing - indicates fluid/secretions in large airways",
    audioFile: "/sounds/lungs/coarse_crackles.mp3",
    useRealAudio: true,
    duration: 12000
  },
  wheezes: {
    name: "Wheezes",
    description: "High-pitched, musical, continuous sounds primarily during expiration. May be heard without stethoscope in severe cases.",
    clinical: "Associated with: Asthma, COPD, bronchospasm, airway obstruction",
    nclex_tip: "Expiratory wheezes = lower airway obstruction. Give bronchodilators!",
    audioFile: "/sounds/lungs/wheezes.mp3",
    useRealAudio: true,
    duration: 12000
  },
  rhonchi: {
    name: "Rhonchi",
    description: "Low-pitched, snoring, rumbling sounds that may clear with coughing. Heard during both inspiration and expiration.",
    clinical: "Associated with: Secretions in large airways, bronchitis, COPD",
    nclex_tip: "Often clears with coughing - encourage coughing and deep breathing",
    audioFile: "/sounds/lungs/rhonchi.mp3",
    useRealAudio: true,
    duration: 12000
  },
  stridor: {
    name: "Stridor",
    description: "High-pitched, harsh sound heard during INSPIRATION. Indicates upper airway obstruction - EMERGENCY!",
    clinical: "Associated with: Croup, epiglottitis, foreign body aspiration, anaphylaxis",
    nclex_tip: "STRIDOR = AIRWAY EMERGENCY! Prepare for intubation.",
    audioFile: "/sounds/lungs/stridor.mp3",
    useRealAudio: true,
    duration: 12000
  },
  pleural_friction_rub: {
    name: "Pleural Friction Rub",
    description: "Grating, creaking sound heard during both inspiration and expiration. Like leather rubbing together.",
    clinical: "Associated with: Pleurisy, pleural effusion, pulmonary embolism",
    nclex_tip: "Sound does NOT change with coughing.",
    audioFile: "/sounds/lungs/pleural_rub.mp3",
    useRealAudio: true,
    duration: 12000
  },
  diminished: {
    name: "Diminished/Absent",
    description: "Decreased or absent breath sounds in an area where sounds should be heard.",
    clinical: "Associated with: Pneumothorax, pleural effusion, atelectasis, COPD",
    nclex_tip: "Unilateral absence = possible pneumothorax - EMERGENCY!",
    useRealAudio: false,
    duration: 12000
  },
  bronchial: {
    name: "Bronchial (Tubular)",
    description: "Loud, high-pitched sounds with equal inspiration/expiration. Normal over trachea, ABNORMAL over lung periphery.",
    clinical: "Associated with: Consolidation (pneumonia), lung collapse",
    nclex_tip: "Bronchial sounds over peripheral lung = consolidation.",
    audioFile: "/sounds/lungs/bronchial.mp3",
    useRealAudio: true,
    duration: 12000
  }
};

// Bowel Sounds Library
const BOWEL_SOUNDS = {
  normal: {
    name: "Normal (Normoactive)",
    description: "Clicks and gurgles occurring 5-30 times per minute. Irregular in timing.",
    clinical: "Normal finding - indicates proper GI motility",
    nclex_tip: "Listen in all 4 quadrants for at least 1 minute each.",
    duration: 15000
  },
  hyperactive: {
    name: "Hyperactive",
    description: "Loud, frequent, high-pitched rushing sounds (borborygmi). >30 sounds per minute.",
    clinical: "Associated with: Diarrhea, early bowel obstruction, GI bleeding",
    nclex_tip: "Early obstruction = hyperactive. Late obstruction = hypoactive/absent",
    duration: 15000
  },
  hypoactive: {
    name: "Hypoactive",
    description: "Infrequent, quiet sounds. <5 sounds per minute.",
    clinical: "Associated with: Post-operative ileus, peritonitis, late obstruction",
    nclex_tip: "Common after abdominal surgery. Hold diet until bowel sounds return.",
    duration: 15000
  },
  absent: {
    name: "Absent",
    description: "No sounds heard after listening for 5 full minutes.",
    clinical: "Associated with: Paralytic ileus, peritonitis, complete obstruction",
    nclex_tip: "ABSENT = NPO, NG tube, notify physician IMMEDIATELY.",
    duration: 12000
  },
  high_pitched: {
    name: "High-Pitched (Tinkling)",
    description: "High-pitched, tinkling sounds with metallic quality.",
    clinical: "Associated with: Early mechanical bowel obstruction",
    nclex_tip: "High-pitched tinkling + distension + vomiting = obstruction",
    duration: 15000
  }
};

// Heart Sounds Library
const HEART_SOUNDS = {
  normal_s1s2: {
    name: "Normal S1-S2",
    description: "S1 (lub) - mitral/tricuspid closure. S2 (dub) - aortic/pulmonic closure.",
    clinical: "Normal finding - proper valve function",
    nclex_tip: "S1 loudest at apex, S2 loudest at base.",
    duration: 10000
  },
  tachycardia: {
    name: "Tachycardia (Rapid S1-S2)",
    description: "Normal S1-S2 at a rapid rate (>100 bpm in adults).",
    clinical: "Associated with: Pain, fever, hypovolemia, hypoxia, sepsis, anxiety",
    nclex_tip: "Tachycardia is an early compensatory sign of shock - look for the cause.",
    duration: 10000
  },
  bradycardia: {
    name: "Bradycardia (Slow S1-S2)",
    description: "Normal S1-S2 at a slow rate (<60 bpm in adults).",
    clinical: "Associated with: Beta-blockers, digoxin toxicity, increased ICP, heart block",
    nclex_tip: "Hold digoxin/beta-blockers and report if HR is below parameters.",
    duration: 10000
  },
  irregular: {
    name: "Irregularly Irregular Rhythm (AFib)",
    description: "S1-S2 with no pattern to the spacing between beats; S1 intensity varies.",
    clinical: "Associated with: Atrial fibrillation - stroke risk from atrial thrombi",
    nclex_tip: "Count an apical pulse for a full 60 seconds; check for a pulse deficit.",
    duration: 10000
  },
  s3_gallop: {
    name: "S3 Gallop (Ventricular Gallop)",
    description: "Low-pitched sound after S2, creating 'lub-dub-ta' or 'Kentucky' rhythm.",
    clinical: "Associated with: Heart failure, fluid overload. Normal in children.",
    nclex_tip: "S3 in adults = HEART FAILURE.",
    duration: 10000
  },
  s4_gallop: {
    name: "S4 Gallop (Atrial Gallop)",
    description: "Low-pitched sound before S1, creating 'ta-lub-dub' or 'Tennessee' rhythm.",
    clinical: "Associated with: Hypertension, CAD, aortic stenosis",
    nclex_tip: "S4 = stiff ventricle from hypertrophy.",
    duration: 10000
  },
  murmur_systolic: {
    name: "Systolic Murmur",
    description: "Swooshing sound between S1 and S2.",
    clinical: "Associated with: Aortic stenosis, mitral regurgitation, VSD",
    nclex_tip: "Systolic = valve stenosis or regurgitation",
    duration: 10000
  },
  murmur_diastolic: {
    name: "Diastolic Murmur",
    description: "Swooshing sound between S2 and S1. Always pathological!",
    clinical: "Associated with: Aortic regurgitation, mitral stenosis",
    nclex_tip: "Diastolic murmurs are ALWAYS abnormal.",
    duration: 10000
  },
  pericardial_rub: {
    name: "Pericardial Friction Rub",
    description: "Scratchy, grating sound in 3 components.",
    clinical: "Associated with: Pericarditis, post-MI",
    nclex_tip: "Rub + chest pain improving leaning forward = pericarditis.",
    duration: 10000
  }
};

// Organic Audio Generator - creates realistic clinical sounds using warm, filtered synthesis
class OrganicAudioGenerator {
  constructor(audioContext) {
    this.ctx = audioContext;
    this.sampleRate = audioContext.sampleRate;
  }

  // Create smooth pink noise (warmer than white noise, no harsh static)
  createPinkNoise(samples) {
    const data = new Float32Array(samples);
    let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0, b6 = 0;
    
    for (let i = 0; i < samples; i++) {
      const white = Math.random() * 2 - 1;
      b0 = 0.99886 * b0 + white * 0.0555179;
      b1 = 0.99332 * b1 + white * 0.0750759;
      b2 = 0.96900 * b2 + white * 0.1538520;
      b3 = 0.86650 * b3 + white * 0.3104856;
      b4 = 0.55000 * b4 + white * 0.5329522;
      b5 = -0.7616 * b5 - white * 0.0168980;
      data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362) * 0.11;
      b6 = white * 0.115926;
    }
    return data;
  }

  // Multi-pass smoothing filter for organic sound
  smoothFilter(data, passes = 3, strength = 0.85) {
    const result = new Float32Array(data.length);
    let current = data;
    
    for (let pass = 0; pass < passes; pass++) {
      const temp = new Float32Array(data.length);
      temp[0] = current[0];
      for (let i = 1; i < data.length; i++) {
        temp[i] = strength * temp[i-1] + (1 - strength) * current[i];
      }
      current = temp;
    }
    
    for (let i = 0; i < data.length; i++) {
      result[i] = current[i];
    }
    return result;
  }

  // Create organic wind-like breath using layered filtered noise with natural drift
  createOrganicBreath(samples, lowCut = 80, highCut = 400) {
    // Start with pink noise
    const pink = this.createPinkNoise(samples);
    
    // Apply multiple stages of filtering to create warm, airy sound
    let filtered = this.smoothFilter(pink, 8, 0.92);
    
    // Apply frequency-dependent shaping
    const shaped = new Float32Array(samples);
    let prevSample = 0;
    let prevPrevSample = 0;
    
    // Resonant low-pass filter simulation
    const resonance = 0.3;
    const cutoffNorm = highCut / this.sampleRate;
    
    for (let i = 0; i < samples; i++) {
      // Add gentle resonance for warmth
      const input = filtered[i];
      const feedback = prevSample * resonance;
      shaped[i] = input * cutoffNorm + prevSample * (1 - cutoffNorm) + feedback * 0.1;
      prevPrevSample = prevSample;
      prevSample = shaped[i];
    }
    
    // Final smoothing pass
    return this.smoothFilter(shaped, 3, 0.88);
  }

  // Natural breath envelope with organic variation
  getOrganicBreathEnvelope(t, breathDuration, inspirationRatio = 0.38) {
    const breathTime = t % breathDuration;
    const phase = breathTime / breathDuration;
    
    // Add subtle timing variation per breath
    const breathNum = Math.floor(t / breathDuration);
    const variation = Math.sin(breathNum * 1.7) * 0.03;
    const adjustedRatio = inspirationRatio + variation;
    
    let envelope;
    if (phase < adjustedRatio) {
      // Inspiration - organic rise with slight acceleration
      const p = phase / adjustedRatio;
      envelope = Math.pow(Math.sin(p * Math.PI * 0.5), 1.2);
    } else if (phase < adjustedRatio + 0.06) {
      // Natural pause at peak inspiration
      const pausePhase = (phase - adjustedRatio) / 0.06;
      envelope = 1.0 - pausePhase * 0.08;
    } else {
      // Expiration - longer, gentler, more passive
      const p = (phase - adjustedRatio - 0.06) / (1 - adjustedRatio - 0.06);
      envelope = Math.pow(1 - p, 1.4) * 0.65;
    }
    
    // Add micro-variations for organic feel
    envelope *= (1 + Math.sin(t * 2.3) * 0.04 + Math.sin(t * 5.7) * 0.02);
    
    return Math.max(0, envelope);
  }

  // Generate organic normal breath sounds - vesicular quality
  generateNormalBreath(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14 + Math.random() * 4;
    const breathDuration = 60 / breathsPerMin;
    
    // Create warm, filtered breath sound
    const breathSound = this.createOrganicBreath(samples, 100, 350);
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const envelope = this.getOrganicBreathEnvelope(t, breathDuration, 0.38);
      
      // Vesicular breath - soft, rustling quality
      data[i] = breathSound[i] * envelope * 0.35;
    }
    
    return buffer;
  }

  // Generate fine crackles - delicate popping in late inspiration
  generateFineCrackles(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14;
    const breathDuration = 60 / breathsPerMin;
    
    // Soft breath base
    const breathSound = this.createOrganicBreath(samples, 120, 300);
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const envelope = this.getOrganicBreathEnvelope(t, breathDuration, 0.4);
      data[i] = breathSound[i] * envelope * 0.2;
    }
    
    // Add fine crackles during late inspiration
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const breathTime = t % breathDuration;
      const phase = breathTime / breathDuration;
      
      // Fine crackles occur in late inspiration (0.28-0.42)
      if (phase > 0.28 && phase < 0.42) {
        if (Math.random() < 0.005) {
          // Create soft, organic crackle - like tiny bubble pops
          const crackleLen = Math.floor(this.sampleRate * (0.004 + Math.random() * 0.005));
          const freq = 600 + Math.random() * 800;
          const intensity = 0.06 + Math.random() * 0.05;
          
          for (let j = 0; j < crackleLen && i + j < samples; j++) {
            const ct = j / this.sampleRate;
            // Soft attack, natural decay
            const attack = 1 - Math.exp(-ct * 800);
            const decay = Math.exp(-ct * 250);
            const env = attack * decay;
            
            // Resonant pop with slight frequency sweep
            const freqSweep = freq * (1 - ct * 50);
            const crackle = Math.sin(2 * Math.PI * freqSweep * ct) * env;
            data[i + j] += crackle * intensity;
          }
        }
      }
    }
    
    return buffer;
  }

  // Generate coarse crackles - wet bubbling sounds
  generateCoarseCrackles(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14;
    const breathDuration = 60 / breathsPerMin;
    
    // Breath base
    const breathSound = this.createOrganicBreath(samples, 100, 280);
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const envelope = this.getOrganicBreathEnvelope(t, breathDuration, 0.4);
      data[i] = breathSound[i] * envelope * 0.18;
    }
    
    // Add coarse crackles - bubbling sounds during both phases
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const breathTime = t % breathDuration;
      const phase = breathTime / breathDuration;
      
      // More likely during inspiration
      const prob = phase < 0.4 ? 0.0025 : 0.0012;
      
      if (Math.random() < prob) {
        // Larger bubble - wet, gurgling quality
        const bubbleLen = Math.floor(this.sampleRate * (0.018 + Math.random() * 0.025));
        const freq = 50 + Math.random() * 70;
        const intensity = 0.1 + Math.random() * 0.08;
        
        for (let j = 0; j < bubbleLen && i + j < samples; j++) {
          const bt = j / this.sampleRate;
          const attack = 1 - Math.exp(-bt * 120);
          const decay = Math.exp(-bt * 35);
          const env = attack * decay;
          
          // Low frequency bubble with harmonics
          let bubble = Math.sin(2 * Math.PI * freq * bt) * 0.55;
          bubble += Math.sin(2 * Math.PI * freq * 1.7 * bt) * 0.28;
          bubble += Math.sin(2 * Math.PI * freq * 2.4 * bt) * 0.12;
          
          data[i + j] += bubble * env * intensity;
        }
      }
    }
    
    return buffer;
  }

  // Generate wheezes - smooth musical whistling
  generateWheezes(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 16;
    const breathDuration = 60 / breathsPerMin;
    
    // Soft breath base
    const breathSound = this.createOrganicBreath(samples, 150, 320);
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const envelope = this.getOrganicBreathEnvelope(t, breathDuration, 0.35);
      data[i] = breathSound[i] * envelope * 0.1;
      
      const breathTime = t % breathDuration;
      const phase = breathTime / breathDuration;
      
      // Wheeze during expiration (0.38 - 0.92)
      if (phase > 0.38 && phase < 0.92) {
        const wheezePhase = (phase - 0.38) / 0.54;
        // Smooth envelope
        let wheezeEnv = Math.sin(wheezePhase * Math.PI);
        wheezeEnv = Math.pow(wheezeEnv, 0.6);
        
        // Musical wheeze with natural vibrato
        const baseFreq = 340 + Math.sin(t * 3.5) * 18 + Math.sin(t * 8) * 6;
        
        let wheeze = Math.sin(2 * Math.PI * baseFreq * t) * 0.5;
        wheeze += Math.sin(2 * Math.PI * baseFreq * 1.5 * t) * 0.25;
        wheeze += Math.sin(2 * Math.PI * baseFreq * 2.0 * t) * 0.15;
        wheeze += Math.sin(2 * Math.PI * baseFreq * 2.5 * t) * 0.08;
        
        data[i] += wheeze * wheezeEnv * 0.14;
      }
    }
    
    return buffer;
  }

  // Generate rhonchi - low rumbling/snoring
  generateRhonchi(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14;
    const breathDuration = 60 / breathsPerMin;
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const envelope = this.getOrganicBreathEnvelope(t, breathDuration, 0.4);
      
      // Low rumbling with natural modulation
      const baseFreq = 62 + Math.sin(t * 1.1) * 10;
      
      let rumble = Math.sin(2 * Math.PI * baseFreq * t) * 0.45;
      rumble += Math.sin(2 * Math.PI * baseFreq * 2 * t) * 0.2;
      rumble += Math.sin(2 * Math.PI * baseFreq * 3 * t) * 0.1;
      rumble += Math.sin(2 * Math.PI * baseFreq * 4.2 * t) * 0.06;
      
      // Snoring modulation
      const snoreFreq = 11 + Math.sin(t * 0.35) * 3;
      rumble *= (1 + Math.sin(2 * Math.PI * snoreFreq * t) * 0.32);
      
      data[i] = rumble * envelope * 0.15;
    }
    
    return buffer;
  }

  // Generate stridor - harsh inspiratory sound
  generateStridor(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 22;
    const breathDuration = 60 / breathsPerMin;
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const breathTime = t % breathDuration;
      const phase = breathTime / breathDuration;
      
      // Stridor during inspiration only
      if (phase < 0.38) {
        const stridorPhase = phase / 0.38;
        const env = Math.sin(stridorPhase * Math.PI);
        
        // Harsh high-pitched sound
        const freq = 400 + Math.sin(t * 9) * 30;
        
        let stridor = Math.sin(2 * Math.PI * freq * t) * 0.4;
        stridor += Math.sin(2 * Math.PI * freq * 1.4 * t) * 0.2;
        stridor += Math.sin(2 * Math.PI * freq * 1.9 * t) * 0.1;
        
        data[i] = stridor * env * 0.2;
      }
    }
    
    return buffer;
  }

  // Generate pleural friction rub
  generatePleuralFrictionRub(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14;
    const breathDuration = 60 / breathsPerMin;
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const breathTime = t % breathDuration;
      const phase = breathTime / breathDuration;
      
      let breathEnv;
      if (phase < 0.4) {
        breathEnv = Math.sin((phase / 0.4) * Math.PI / 2);
      } else if (phase < 0.45) {
        breathEnv = 0.08;
      } else {
        breathEnv = Math.sin(((phase - 0.45) / 0.55) * Math.PI) * 0.7;
      }
      
      // Creaking leather sound
      if (breathEnv > 0.1) {
        const creakFreq = 200 + Math.sin(t * 55) * 70;
        
        let rub = Math.sin(2 * Math.PI * creakFreq * t + Math.sin(t * 100) * 1.0) * 0.35;
        rub += Math.sin(2 * Math.PI * creakFreq * 1.5 * t) * 0.12;
        rub *= (1 + Math.sin(t * 350) * 0.2);
        
        data[i] = rub * breathEnv * 0.16;
      }
    }
    
    return buffer;
  }

  // Generate diminished breath sounds
  generateDiminished(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14;
    const breathDuration = 60 / breathsPerMin;
    
    // Very soft breath
    const breathSound = this.createOrganicBreath(samples, 150, 280);
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      let envelope = this.getOrganicBreathEnvelope(t, breathDuration, 0.4);
      // Greatly reduced
      envelope *= 0.25;
      
      data[i] = breathSound[i] * envelope * 0.08;
    }
    
    return buffer;
  }

  // Generate bronchial breath sounds
  generateBronchial(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const breathsPerMin = 14;
    const breathDuration = 60 / breathsPerMin;
    
    // Louder, tubular breath
    const breathSound = this.createOrganicBreath(samples, 120, 450);
    
    for (let i = 0; i < samples; i++) {
      const t = i / this.sampleRate;
      const breathTime = t % breathDuration;
      const phase = breathTime / breathDuration;
      
      // Equal inspiration/expiration
      let envelope;
      if (phase < 0.45) {
        envelope = Math.sin((phase / 0.45) * Math.PI / 2);
      } else if (phase < 0.5) {
        envelope = 0.12;
      } else {
        envelope = Math.sin(((phase - 0.5) / 0.5) * Math.PI / 2) * 0.85;
      }
      
      // Add hollow tubular resonance
      const tubeFreq = 240 + Math.sin(t * 1.5) * 15;
      let tubular = Math.sin(2 * Math.PI * tubeFreq * t) * 0.12;
      tubular += Math.sin(2 * Math.PI * tubeFreq * 1.5 * t) * 0.05;
      
      data[i] = (breathSound[i] * 0.6 + tubular) * envelope * 0.2;
    }
    
    return buffer;
  }

  // Generate normal bowel sounds - organic gurgles
  generateNormalBowel(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Very subtle ambient (near silence)
    const pinkNoise = this.createPinkNoise(samples);
    const ambient = this.smoothFilter(pinkNoise, 6, 0.95);
    for (let i = 0; i < samples; i++) {
      data[i] = ambient[i] * 0.003;
    }
    
    // Random gurgles every 3-8 seconds
    let nextSound = Math.random() * this.sampleRate * 2;
    while (nextSound < samples) {
      if (Math.random() < 0.7) {
        // Gurgle - warm, resonant
        const length = Math.floor(this.sampleRate * (0.15 + Math.random() * 0.25));
        const freq = 100 + Math.random() * 160;
        
        for (let j = 0; j < length && nextSound + j < samples; j++) {
          const gt = j / this.sampleRate;
          const env = Math.sin((j / length) * Math.PI);
          const freqMod = freq + Math.sin(gt * 20) * 35;
          
          let gurgle = Math.sin(2 * Math.PI * freqMod * gt) * 0.45;
          gurgle += Math.sin(2 * Math.PI * freqMod * 1.5 * gt) * 0.22;
          gurgle += Math.sin(2 * Math.PI * freqMod * 2.2 * gt) * 0.1;
          
          data[Math.floor(nextSound) + j] += gurgle * env * 0.18;
        }
      } else {
        // Soft click - resonant pop
        const length = Math.floor(this.sampleRate * 0.04);
        const clickFreq = 200 + Math.random() * 150;
        for (let j = 0; j < length && nextSound + j < samples; j++) {
          const ct = j / this.sampleRate;
          const env = Math.exp(-ct * 80) * (1 - Math.exp(-ct * 500));
          data[Math.floor(nextSound) + j] += Math.sin(2 * Math.PI * clickFreq * ct) * env * 0.12;
        }
      }
      
      nextSound += this.sampleRate * (3 + Math.random() * 5);
    }
    
    return buffer;
  }

  // Generate hyperactive bowel - frequent rushing sounds
  generateHyperactiveBowel(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Subtle ambient
    const pinkNoise = this.createPinkNoise(samples);
    const ambient = this.smoothFilter(pinkNoise, 6, 0.95);
    for (let i = 0; i < samples; i++) {
      data[i] = ambient[i] * 0.003;
    }
    
    // Frequent rushing sounds every 1-2 seconds
    let nextSound = Math.random() * this.sampleRate * 0.5;
    while (nextSound < samples) {
      const length = Math.floor(this.sampleRate * (0.2 + Math.random() * 0.35));
      const freq = 130 + Math.random() * 200;
      
      for (let j = 0; j < length && nextSound + j < samples; j++) {
        const rt = j / this.sampleRate;
        const env = Math.sin((j / length) * Math.PI);
        
        let rush = Math.sin(2 * Math.PI * (freq + Math.sin(rt * 30) * 50) * rt) * 0.4;
        rush += Math.sin(2 * Math.PI * freq * 1.4 * rt) * 0.22;
        rush += Math.sin(2 * Math.PI * freq * 1.9 * rt) * 0.1;
        
        data[Math.floor(nextSound) + j] += rush * env * 0.28;
      }
      
      nextSound += this.sampleRate * (1 + Math.random() * 1.5);
    }
    
    return buffer;
  }

  // Generate hypoactive bowel - rare, quiet sounds
  generateHypoactiveBowel(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Very subtle ambient
    const pinkNoise = this.createPinkNoise(samples);
    const ambient = this.smoothFilter(pinkNoise, 7, 0.96);
    for (let i = 0; i < samples; i++) {
      data[i] = ambient[i] * 0.002;
    }
    
    // Rare sounds every 12-20 seconds
    let nextSound = this.sampleRate * (8 + Math.random() * 5);
    while (nextSound < samples) {
      const length = Math.floor(this.sampleRate * (0.1 + Math.random() * 0.1));
      const freq = 90 + Math.random() * 70;
      
      for (let j = 0; j < length && nextSound + j < samples; j++) {
        const gt = j / this.sampleRate;
        const env = Math.sin((j / length) * Math.PI) * 0.35;
        data[Math.floor(nextSound) + j] += Math.sin(2 * Math.PI * freq * gt) * env * 0.08;
      }
      
      nextSound += this.sampleRate * (12 + Math.random() * 10);
    }
    
    return buffer;
  }

  // Generate absent bowel - near silence
  generateAbsentBowel(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Just very faint ambient
    const pinkNoise = this.createPinkNoise(samples);
    const ambient = this.smoothFilter(pinkNoise, 7, 0.97);
    for (let i = 0; i < samples; i++) {
      data[i] = ambient[i] * 0.001;
    }
    
    return buffer;
  }

  // Generate tinkling bowel - metallic high-pitched sounds
  generateTinklingBowel(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    // Subtle ambient
    const pinkNoise = this.createPinkNoise(samples);
    const ambient = this.smoothFilter(pinkNoise, 6, 0.95);
    for (let i = 0; i < samples; i++) {
      data[i] = ambient[i] * 0.002;
    }
    
    // High-pitched metallic tinkles
    let nextSound = Math.random() * this.sampleRate * 1.5;
    while (nextSound < samples) {
      const length = Math.floor(this.sampleRate * (0.25 + Math.random() * 0.35));
      const freq = 650 + Math.random() * 450;
      
      for (let j = 0; j < length && nextSound + j < samples; j++) {
        const tt = j / this.sampleRate;
        const env = Math.exp(-tt * 5) * (1 - Math.exp(-tt * 60));
        
        // Bell-like resonant sound
        let tinkle = Math.sin(2 * Math.PI * freq * tt) * 0.35;
        tinkle += Math.sin(2 * Math.PI * freq * 2.4 * tt) * 0.18;
        tinkle += Math.sin(2 * Math.PI * freq * 3.7 * tt) * 0.1;
        
        data[Math.floor(nextSound) + j] += tinkle * env * 0.22;
      }
      
      nextSound += this.sampleRate * (2 + Math.random() * 3);
    }
    
    return buffer;
  }

  // Generate normal heart sounds - clean S1/S2
  generateNormalHeart(durationSec, bpmOverride = null, irregular = false) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const bpm = bpmOverride || 72 + Math.random() * 8;
    const baseBeat = 60 / bpm;
    let t = 0;
    
    while (t < durationSec) {
      // Irregularly irregular R-R intervals for atrial fibrillation
      const beatDuration = irregular ? baseBeat * (0.55 + Math.random() * 0.9) : baseBeat;
      const beatStart = Math.floor(t * this.sampleRate);
      t += beatDuration;
      
      // S1 - lub (warm, resonant)
      const s1Length = Math.floor(this.sampleRate * 0.12);
      for (let j = 0; j < s1Length && beatStart + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 25) * (1 - Math.exp(-st * 300));
        
        let s1 = Math.sin(2 * Math.PI * 45 * st) * 0.5;
        s1 += Math.sin(2 * Math.PI * 75 * st) * 0.35;
        s1 += Math.sin(2 * Math.PI * 110 * st) * 0.2;
        
        data[beatStart + j] += s1 * env * 0.4;
      }
      
      // S2 - dub (slightly higher, shorter)
      const s2Start = beatStart + Math.floor(beatDuration * 0.35 * this.sampleRate);
      const s2Length = Math.floor(this.sampleRate * 0.08);
      for (let j = 0; j < s2Length && s2Start + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 35) * (1 - Math.exp(-st * 400));
        
        let s2 = Math.sin(2 * Math.PI * 55 * st) * 0.4;
        s2 += Math.sin(2 * Math.PI * 90 * st) * 0.3;
        
        data[s2Start + j] += s2 * env * 0.35;
      }
    }
    
    return buffer;
  }

  // Generate S3 gallop - "Kentucky" rhythm
  generateS3Gallop(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const bpm = 80;
    const beatDuration = 60 / bpm;
    
    for (let beat = 0; beat * beatDuration < durationSec; beat++) {
      const beatStart = Math.floor(beat * beatDuration * this.sampleRate);
      
      // S1
      for (let j = 0; j < this.sampleRate * 0.12 && beatStart + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 25) * (1 - Math.exp(-st * 300));
        data[beatStart + j] += Math.sin(2 * Math.PI * 50 * st) * env * 0.35;
      }
      
      // S2
      const s2Start = beatStart + Math.floor(beatDuration * 0.35 * this.sampleRate);
      for (let j = 0; j < this.sampleRate * 0.08 && s2Start + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 35) * (1 - Math.exp(-st * 400));
        data[s2Start + j] += Math.sin(2 * Math.PI * 60 * st) * env * 0.3;
      }
      
      // S3 - low thud after S2
      const s3Start = beatStart + Math.floor(beatDuration * 0.48 * this.sampleRate);
      for (let j = 0; j < this.sampleRate * 0.06 && s3Start + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 50) * (1 - Math.exp(-st * 500));
        data[s3Start + j] += Math.sin(2 * Math.PI * 30 * st) * env * 0.2;
      }
    }
    
    return buffer;
  }

  // Generate S4 gallop - "Tennessee" rhythm
  generateS4Gallop(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const bpm = 72;
    const beatDuration = 60 / bpm;
    
    for (let beat = 0; beat * beatDuration < durationSec; beat++) {
      const beatStart = Math.floor(beat * beatDuration * this.sampleRate);
      
      // S4 before S1
      const s4Start = beatStart - Math.floor(beatDuration * 0.1 * this.sampleRate);
      if (s4Start > 0) {
        for (let j = 0; j < this.sampleRate * 0.05 && s4Start + j < samples; j++) {
          const st = j / this.sampleRate;
          const env = Math.exp(-st * 60) * (1 - Math.exp(-st * 600));
          data[s4Start + j] += Math.sin(2 * Math.PI * 25 * st) * env * 0.15;
        }
      }
      
      // S1
      for (let j = 0; j < this.sampleRate * 0.12 && beatStart + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 25) * (1 - Math.exp(-st * 300));
        data[beatStart + j] += Math.sin(2 * Math.PI * 50 * st) * env * 0.35;
      }
      
      // S2
      const s2Start = beatStart + Math.floor(beatDuration * 0.35 * this.sampleRate);
      for (let j = 0; j < this.sampleRate * 0.08 && s2Start + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 35) * (1 - Math.exp(-st * 400));
        data[s2Start + j] += Math.sin(2 * Math.PI * 60 * st) * env * 0.3;
      }
    }
    
    return buffer;
  }

  // Generate systolic murmur - turbulent flow between S1 and S2
  generateSystolicMurmur(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const bpm = 76;
    const beatDuration = 60 / bpm;
    
    for (let beat = 0; beat * beatDuration < durationSec; beat++) {
      const beatStart = Math.floor(beat * beatDuration * this.sampleRate);
      
      // S1 (softer)
      for (let j = 0; j < this.sampleRate * 0.1 && beatStart + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 30) * (1 - Math.exp(-st * 350));
        data[beatStart + j] += Math.sin(2 * Math.PI * 50 * st) * env * 0.25;
      }
      
      // Systolic murmur - smooth crescendo-decrescendo
      const murmurStart = beatStart + Math.floor(this.sampleRate * 0.1);
      const murmurEnd = beatStart + Math.floor(beatDuration * 0.32 * this.sampleRate);
      const murmurLength = murmurEnd - murmurStart;
      
      for (let j = 0; j < murmurLength && murmurStart + j < samples; j++) {
        const phase = j / murmurLength;
        const env = Math.sin(phase * Math.PI) * 0.28;
        const mt = j / this.sampleRate;
        
        // Smooth turbulent sound using layered frequencies
        let murmur = Math.sin(2 * Math.PI * 180 * mt) * 0.12;
        murmur += Math.sin(2 * Math.PI * 240 * mt + Math.sin(mt * 50) * 0.5) * 0.08;
        murmur += Math.sin(2 * Math.PI * 320 * mt) * 0.05;
        
        data[murmurStart + j] += murmur * env;
      }
      
      // S2
      const s2Start = beatStart + Math.floor(beatDuration * 0.35 * this.sampleRate);
      for (let j = 0; j < this.sampleRate * 0.08 && s2Start + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 35) * (1 - Math.exp(-st * 400));
        data[s2Start + j] += Math.sin(2 * Math.PI * 60 * st) * env * 0.25;
      }
    }
    
    return buffer;
  }

  // Generate diastolic murmur - decrescendo after S2
  generateDiastolicMurmur(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const bpm = 72;
    const beatDuration = 60 / bpm;
    
    for (let beat = 0; beat * beatDuration < durationSec; beat++) {
      const beatStart = Math.floor(beat * beatDuration * this.sampleRate);
      
      // S1
      for (let j = 0; j < this.sampleRate * 0.12 && beatStart + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 25) * (1 - Math.exp(-st * 300));
        data[beatStart + j] += Math.sin(2 * Math.PI * 50 * st) * env * 0.3;
      }
      
      // S2
      const s2Start = beatStart + Math.floor(beatDuration * 0.35 * this.sampleRate);
      for (let j = 0; j < this.sampleRate * 0.08 && s2Start + j < samples; j++) {
        const st = j / this.sampleRate;
        const env = Math.exp(-st * 35) * (1 - Math.exp(-st * 400));
        data[s2Start + j] += Math.sin(2 * Math.PI * 60 * st) * env * 0.25;
      }
      
      // Diastolic murmur - smooth decrescendo after S2
      const murmurStart = s2Start + Math.floor(this.sampleRate * 0.06);
      const murmurLength = Math.floor(beatDuration * 0.45 * this.sampleRate);
      
      for (let j = 0; j < murmurLength && murmurStart + j < samples; j++) {
        const phase = j / murmurLength;
        const env = Math.exp(-phase * 2.5) * 0.22;
        const mt = j / this.sampleRate;
        
        // Smooth turbulent sound
        let murmur = Math.sin(2 * Math.PI * 160 * mt) * 0.1;
        murmur += Math.sin(2 * Math.PI * 200 * mt + Math.sin(mt * 40) * 0.4) * 0.06;
        
        data[murmurStart + j] += murmur * env;
      }
    }
    
    return buffer;
  }

  // Generate pericardial rub - scratchy grating sound
  generatePericardialRub(durationSec) {
    const samples = Math.floor(durationSec * this.sampleRate);
    const buffer = this.ctx.createBuffer(1, samples, this.sampleRate);
    const data = buffer.getChannelData(0);
    
    const bpm = 80;
    const beatDuration = 60 / bpm;
    
    for (let beat = 0; beat * beatDuration < durationSec; beat++) {
      const beatStart = Math.floor(beat * beatDuration * this.sampleRate);
      
      // Three scratchy components
      const timings = [0, 0.18, 0.55];
      
      timings.forEach((timing, idx) => {
        const rubStart = beatStart + Math.floor(beatDuration * timing * this.sampleRate);
        const rubLength = Math.floor(this.sampleRate * (0.05 + Math.random() * 0.025));
        
        for (let j = 0; j < rubLength && rubStart + j < samples; j++) {
          const rt = j / this.sampleRate;
          const env = Math.sin((j / rubLength) * Math.PI) * (0.22 - idx * 0.035);
          
          // Grating sound - frequency modulated
          let rub = Math.sin(2 * Math.PI * 280 * rt + Math.sin(rt * 120) * 1.8) * 0.18;
          rub += Math.sin(2 * Math.PI * 350 * rt) * 0.08;
          
          data[rubStart + j] += rub * env;
        }
      });
    }
    
    return buffer;
  }

  // Main generate function
  generate(type, soundKey, duration) {
    const durationSec = duration / 1000;
    
    if (type === 'lungs') {
      switch(soundKey) {
        case 'normal': return this.generateNormalBreath(durationSec);
        case 'crackles_fine': return this.generateFineCrackles(durationSec);
        case 'crackles_coarse': return this.generateCoarseCrackles(durationSec);
        case 'wheezes': return this.generateWheezes(durationSec);
        case 'rhonchi': return this.generateRhonchi(durationSec);
        case 'stridor': return this.generateStridor(durationSec);
        case 'pleural_friction_rub': return this.generatePleuralFrictionRub(durationSec);
        case 'diminished': return this.generateDiminished(durationSec);
        case 'bronchial': return this.generateBronchial(durationSec);
        default: return this.generateNormalBreath(durationSec);
      }
    } else if (type === 'bowels') {
      switch(soundKey) {
        case 'normal': return this.generateNormalBowel(durationSec);
        case 'hyperactive': return this.generateHyperactiveBowel(durationSec);
        case 'hypoactive': return this.generateHypoactiveBowel(durationSec);
        case 'absent': return this.generateAbsentBowel(durationSec);
        case 'high_pitched': return this.generateTinklingBowel(durationSec);
        default: return this.generateNormalBowel(durationSec);
      }
    } else {
      switch(soundKey) {
        case 'normal_s1s2': return this.generateNormalHeart(durationSec);
        case 'tachycardia': return this.generateNormalHeart(durationSec, 125);
        case 'bradycardia': return this.generateNormalHeart(durationSec, 48);
        case 'irregular': return this.generateNormalHeart(durationSec, 105, true);
        case 's3_gallop': return this.generateS3Gallop(durationSec);
        case 's4_gallop': return this.generateS4Gallop(durationSec);
        case 'murmur_systolic': return this.generateSystolicMurmur(durationSec);
        case 'murmur_diastolic': return this.generateDiastolicMurmur(durationSec);
        case 'pericardial_rub': return this.generatePericardialRub(durationSec);
        default: return this.generateNormalHeart(durationSec);
      }
    }
  }
}

// Main Component
export const AuscultationPlayer = ({ 
  type = 'lungs',
  soundKey,
  onComplete,
  showInfo = true,
  size = 'default'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const generatorRef = useRef(null);
  const audioElementRef = useRef(null);
  
  const sounds = type === 'lungs' ? LUNG_SOUNDS : type === 'bowels' ? BOWEL_SOUNDS : HEART_SOUNDS;
  const soundConfig = sounds[soundKey] || sounds[Object.keys(sounds)[0]];
  
  useEffect(() => {
    return () => {
      // Cleanup procedural audio
      if (sourceRef.current) {
        try { sourceRef.current.stop(); } catch(e) {}
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
      // Cleanup real audio
      if (audioElementRef.current) {
        audioElementRef.current.pause();
        audioElementRef.current = null;
      }
    };
  }, []);
  
  const playSound = async () => {
    if (isPlaying) {
      stopSound();
      return;
    }
    
    try {
      // Check if this sound uses real audio file
      if (soundConfig.useRealAudio && soundConfig.audioFile) {
        // Use real audio file
        const audio = new Audio(soundConfig.audioFile);
        audio.loop = true;
        audioElementRef.current = audio;
        
        audio.addEventListener('canplaythrough', () => {
          audio.play();
          setIsPlaying(true);
        });
        
        audio.addEventListener('error', (e) => {
          console.error('Audio file error:', e);
          // Fallback to procedural if file fails
          playProceduralSound();
        });
        
        audio.load();
        
        // Auto-stop after duration
        setTimeout(() => {
          if (audioElementRef.current) {
            audioElementRef.current.pause();
            audioElementRef.current = null;
          }
          setIsPlaying(false);
          if (onComplete) onComplete();
        }, soundConfig.duration * 1.5);
        
      } else {
        // Use procedural synthesis
        playProceduralSound();
      }
    } catch (error) {
      console.error('Audio error:', error);
      setIsPlaying(false);
    }
  };
  
  const playProceduralSound = async () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        generatorRef.current = new OrganicAudioGenerator(audioContextRef.current);
      }
      
      const audioContext = audioContextRef.current;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      const buffer = generatorRef.current.generate(type, soundKey, soundConfig.duration);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      
      const gainNode = audioContext.createGain();
      gainNode.gain.value = 1.0;
      
      source.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      source.loop = true;
      sourceRef.current = source;
      source.start();
      setIsPlaying(true);
      
      setTimeout(() => {
        if (sourceRef.current) {
          try { sourceRef.current.stop(); } catch(e) {}
        }
        setIsPlaying(false);
        if (onComplete) onComplete();
      }, soundConfig.duration * 1.5);
    } catch (error) {
      console.error('Procedural audio error:', error);
      setIsPlaying(false);
    }
  };
  
  const stopSound = () => {
    // Stop real audio
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current = null;
    }
    // Stop procedural audio
    if (sourceRef.current) {
      try { sourceRef.current.stop(); } catch(e) {}
    }
    setIsPlaying(false);
  };
  
  const sizeClasses = {
    small: 'p-2 text-xs',
    default: 'p-3 text-sm',
    large: 'p-4 text-base'
  };
  
  return (
    <div className={`bg-slate-50 rounded-lg border ${sizeClasses[size]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Volume2 className={`${size === 'small' ? 'w-4 h-4' : 'w-5 h-5'} text-slate-600`} />
          <span className="font-medium text-slate-800">{soundConfig.name}</span>
        </div>
        <div className="flex items-center gap-1">
          {showInfo && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowDetails(!showDetails)}
              className="h-7 w-7 p-0"
            >
              <Info className="w-4 h-4 text-blue-500" />
            </Button>
          )}
          <Button
            size="sm"
            variant={isPlaying ? "destructive" : "default"}
            onClick={isPlaying ? stopSound : playSound}
            className={`${size === 'small' ? 'h-7 px-2' : 'h-8 px-3'}`}
          >
            {isPlaying ? (
              <>
                <Square className="w-3 h-3 mr-1" />
                Stop
              </>
            ) : (
              <>
                <Play className="w-3 h-3 mr-1" />
                Play
              </>
            )}
          </Button>
        </div>
      </div>
      
      {showDetails && (
        <div className="mt-2 p-2 bg-white rounded border text-xs space-y-1">
          <p className="text-slate-600"><strong>Description:</strong> {soundConfig.description}</p>
          <p className="text-slate-600"><strong>Clinical:</strong> {soundConfig.clinical}</p>
          {soundConfig.nclex_tip && (
            <p className="text-blue-700 bg-blue-50 p-1 rounded">
              <strong>NCLEX Tip:</strong> {soundConfig.nclex_tip}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export const SoundLibrary = ({ type = 'lungs' }) => {
  const sounds = type === 'lungs' ? LUNG_SOUNDS : type === 'bowels' ? BOWEL_SOUNDS : HEART_SOUNDS;
  const title = type === 'lungs' ? 'Lung Sounds' : type === 'bowels' ? 'Bowel Sounds' : 'Heart Sounds';
  
  return (
    <div className="space-y-3">
      <h3 className="font-bold text-lg text-slate-800">{title} Library</h3>
      <div className="grid gap-2">
        {Object.entries(sounds).map(([key]) => (
          <AuscultationPlayer key={key} type={type} soundKey={key} showInfo={true} size="default" />
        ))}
      </div>
    </div>
  );
};

export { LUNG_SOUNDS, BOWEL_SOUNDS, HEART_SOUNDS };
export default AuscultationPlayer;
